<template>
	<section>
		<section class="pdf-print-container" ref="pages">
			<PdfHeader
				@ready="pdfHeader = true"
				:currCompany="currCompany"
				:currSite="currSite"
				:currRoom="currRoom"
				:filteredShifts="filteredShifts"
				:time="filterSelected.time"
				:titleI18nKey="pdfTitleI18nKey"
				:includeRoomLine="includeRoomLineInPdf"
			/>
			<section class="info-bar" ref="barItem">
				<BarItem
					v-for="(item, index) in infoBarItems"
					:key="item.label"
					:isAmended="item.isAmended"
					:trendInfo="item.trendInfo"
					componentType="info"
					:text="item.text"
					:subText="item.subText"
					:hint="item.hint"
					:label="$t(item.label)"
					:iconUrl="item.iconUrl"
					:class="{ last: index === infoBarItems.length - 1, first: index === 0 }"
					@mounted="barItem = true"
				/>
			</section>
			<AnalyticsChartTable
				:data="analyticsData"
				:expandDetails="true"
				class="analytics-data"
				@mounted="analyticsChart = true"
				ref="analytics"
			/>
		</section>
		<TableByHour
			:dataByHour="hourData"
			:isExporting="true"
			class="hour-data"
			@mounted="tableByHour = true"
			ref="hourTable"
		/>
		<div class="page-break" :dir="isRTL ? 'rtl' : 'ltr'">
			<RoomTable
				:isExporting="true"
				:hideCols="hideInfo"
				:rooms="roomsData || []"
				class="room-data"
				@mounted="roomTable = true"
				ref="roomTable"
			/>
		</div>
	</section>
</template>

<script>
import BarItem from './BarItem'
import AnalyticsChartTable from './AnalyticsChartTable'
import RoomTable from './RoomTable'
import TableByHour from './TableByHour'
import PdfHeader from '../../../PDF/PdfHeader'
import html2pdf from 'html2pdf.js'
import { mapGetters } from 'vuex'
import Swal from 'sweetalert2'

export default {
	name: 'analytics-print',
	props: {
		infoBarItems: { type: Array, required: true },
		analyticsData: { type: Object, required: true },
		hourData: { type: Array, required: true },
		hideInfo: { type: Array, required: false },
		roomsData: { type: Array, required: false },
		/** i18n key for PdfHeader main title */
		pdfTitleI18nKey: { type: String, default: 'pdf_export_title_analytics' },
		/** When true, show room name in PdfHeader (e.g. room analytics log export) */
		includeRoomLineInPdf: { type: Boolean, default: false },
		/** Appended to PDF filename after site name (e.g. room name), sanitized */
		pdfFilenameSuffix: { type: String, default: '' },
	},
	data() {
		return {
			pdfHeader: false,
			barItem: false,
			analyticsChart: false,
			tableByHour: false,
			roomTable: false,
			exportScheduleTimer: null,
			pdfGenerating: false,
		}
	},
	computed: {
		...mapGetters(['isRTL', 'filterSelected', 'filterOptions']),
		loadedComps() {
			const { pdfHeader, barItem, analyticsChart, tableByHour, roomTable } = this
			return pdfHeader && barItem && analyticsChart && tableByHour && roomTable
		},
		currCompany() {
			const { companies } = this.filterOptions
			return companies.find((c) => c.id === this.filterSelected.company)
		},
		currSite() {
			const { site: siteId } = this.filterSelected
			const { sites } = this.currCompany
			const foundSite = sites.find((site) => site.id === siteId)
			if (foundSite) return foundSite
			else return null
		},
		currRoom() {
			const { rooms } = this.filterOptions
			const { roomId } = this.$route.params
			for (const key in rooms) {
				if (rooms[key].id === +roomId) return rooms[key]
			}
			return null
		},
		filteredShifts() {
			const { shift: shifts } = this.filterOptions
			const { shift: selectedShifts } = this.filterSelected
			if (!selectedShifts) return []
			return selectedShifts.map((id) => shifts.find((shift) => shift.id === id))
		},
	},
	watch: {
		loadedComps: {
			handler(newVal) {
				if (!newVal) {
					if (this.exportScheduleTimer) {
						clearTimeout(this.exportScheduleTimer)
						this.exportScheduleTimer = null
					}
					return
				}
				if (this.pdfGenerating) return
				if (this.exportScheduleTimer) {
					clearTimeout(this.exportScheduleTimer)
				}
				this.exportScheduleTimer = setTimeout(() => {
					this.exportScheduleTimer = null
					this.generatePDF()
				}, 800)
			},
			deep: true,
			immediate: true
		}
	},
	beforeDestroy() {
		if (this.exportScheduleTimer) {
			clearTimeout(this.exportScheduleTimer)
			this.exportScheduleTimer = null
		}
		document.body.classList.remove('print-mode')
	},
	methods: {
		waitForPrintPaint() {
			return this.$nextTick().then(
				() =>
					new Promise((resolve) => {
						requestAnimationFrame(() => {
							requestAnimationFrame(() => {
								setTimeout(resolve, 350)
							})
						})
					})
			);
		},
		buildPdfOpts(filename) {
			return {
				margin: [0, 0.1],
				filename,
				image: { type: 'jpeg', quality: 0.95 },
				jsPDF: { unit: 'in', format: 'a3', orientation: 'landscape' },
				html2canvas: {
					scale: 1.5,
					useCORS: true,
					logging: false,
					scrollX: 0,
					scrollY: 0,
				},
			}
		},
		/**
		 * Safe download name for html2pdf + browser save dialog. Must be non-empty and end with .pdf
		 * (final .save(filename) re-applies this — required after multi-step chain or name can be missing).
		 */
		buildExportFilename() {
			const safe = (val, fallback) => {
				if (val === undefined || val === null) return fallback;
				const s = String(val)
					.replace(/[/\\:*?"<>|]/g, '-')
					.replace(/\s{2,}/g, ' ')
					.trim();
				return s || fallback;
			};
			const company = safe(this.currCompany && this.currCompany.name, 'Company');
			const site = safe(this.currSite && this.currSite.name, 'Site');
			const suffix = this.pdfFilenameSuffix
				? `-${safe(this.pdfFilenameSuffix, 'room')}`
				: '';
			const time = this.filterSelected && this.filterSelected.time;
			const from = time ? safe(time.timeFrom, '') : '';
			const to = time ? safe(time.timeTo, '') : '';
			const range =
				from && to
					? `${from}-${to}`
					: safe(from || to || new Date().toISOString().slice(0, 10), 'export');
			let base = `${company}-${site}${suffix}-${range}`.replace(/-+/g, '-').replace(/^[-.]+|[-.]+$/g, '');
			if (!base) base = 'analytics-export';
			return /\.pdf$/i.test(base) ? base : `${base}.pdf`;
		},
		async generatePDF() {
			if (this.pdfGenerating) return
			this.pdfGenerating = true

			if (this.isRTL) {
				for (const cmp in this.$refs) {
					if (this.$refs[cmp].$el === undefined) this.$refs[cmp].classList.add('rtl')
					else this.$refs[cmp].$el.classList.add('rtl')
				}
			}

			const pages = this.$refs.pages
			const roomTableRef = this.$refs.roomTable
			const hourTableRef = this.$refs.hourTable
			const roomTableEl = roomTableRef && roomTableRef.$el
			const hourTableEl = hourTableRef && hourTableRef.$el

			const filename = this.buildExportFilename()
			const pagebreak = { pagebreak: { mode: ['avoid-all', 'css', 'legacy'] } }

			document.body.classList.add('print-mode')

			try {
				if (!pages || !roomTableEl || !hourTableEl) {
					throw new Error('AnalyticsPrint: missing DOM refs for PDF export')
				}

				await this.waitForPrintPaint()

				const baseOpts = this.buildPdfOpts(filename)
				await html2pdf()
					.set(baseOpts)
					.from(pages)
					.toPdf()
					.get('pdf')
					.then((pdf) => {
						pdf.addPage()
					})
					.set(pagebreak)
					.from(hourTableEl)
					.toContainer()
					.toCanvas()
					.toPdf()
					.get('pdf')
					.then((pdf) => {
						pdf.addPage()
					})
					.set(pagebreak)
					.from(roomTableEl)
					.toContainer()
					.toCanvas()
					.toPdf()
					.save(filename)

				this.$emit('donePrinting')
			} catch (err) {
				console.error('[AnalyticsPrint] PDF export failed', err)
				Swal.fire({
					icon: 'error',
					title: 'Oops...',
					text: this.$t('data_export_failed'),
					zIndex: 10000,
				})
				this.$emit('donePrinting')
			} finally {
				document.body.classList.remove('print-mode')
				this.pdfGenerating = false
			}
		}
	},
	components: {
		BarItem,
		AnalyticsChartTable,
		RoomTable,
		TableByHour,
		PdfHeader
	}
}
</script>

<style lang="scss">
.pdf-print-container {
	display: flex;
	flex-direction: column;
	height: max-content;
	gap: 60px;

	.info-bar {
		display: flex;
		flex-wrap: wrap;
		flex-direction: row;
		align-self: center;

		.card {
			.label-container {
				font-size: 11px;
			}
		}
	}

	.rtl {
		direction: rtl;
	}
}

.print-mode [dir='rtl'] .room-data,
.print-mode [dir='rtl'] .hour-data {
	direction: rtl;
}

.page-break {
	page-break-before: always;
	break-before: page;
}

</style>
